import { Card, Badge, Button } from "components/common";
import { FaAddressCard, FaGg, FaIndustry, FaInfo, FaLink, FaMap, FaPhone, FaRProject, FaRecordVinyl } from "react-icons/fa";
import { MdAccountBalance, MdBloodtype, MdEmail, MdMessage, MdTransferWithinAStation } from "react-icons/md";
import { Link } from "react-router-dom";
import { formatPhoneNumberInternational, formatPhoneNumberUrl } from 'helpers/phoneNumber';
import { formatNumber } from "helpers/utils";
import GoogleMapsLink from "helpers/googleMapsLink";
import React, { useState, Suspense, lazy } from "react";
import ContactItem from "../../ContactItem";
import RatingScore from "../../component/RatingScore";
import PropTypes from 'prop-types';

const LazyLeadTransferToSalesConfirm = lazy(() => import("../confirmation/LeadTransferToSalesConfirm"));
const LazyLeadRecordConfirm = lazy(() => import("../confirmation/LeadRecordConfirm"));

// Static class pairs so Tailwind can see the full class names at build time
const SPAN_CLASSES = {
  2: ['sm:tw-col-span-2', 'sm:tw-col-span-10'],
  3: ['sm:tw-col-span-3', 'sm:tw-col-span-9'],
  5: ['sm:tw-col-span-5', 'sm:tw-col-span-7'],
  6: ['sm:tw-col-span-6', 'sm:tw-col-span-6'],
};

const infoRow = (label, value, labelSpan = 3) => {
  const [labelClass, valueClass] = SPAN_CLASSES[labelSpan] || SPAN_CLASSES[3];

  return (
    <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
      <div className={`tw-col-span-12 ${labelClass}`}>{label}</div>
      <div className={`tw-col-span-12 ${valueClass}`}>
        {value}
      </div>
    </div>
  );
};

const Open = ({lead}) => {
  const status = lead?.status;
  const permissions = lead?.permissions;
  const contacts = lead?.contacts;

  const {
    allow_transfer,
    allow_record,
  } = permissions || {};

  const [showRecordConfirm, setShowRecordConfirm] = useState(false);
  const [showTransferToSalesConfirm, setShowTransferToSalesConfirm] = useState(false);

  const handleRecordLead = () => {
    setShowRecordConfirm(true);
  }

  const handleTransferToSalesLead = () => {
    setShowTransferToSalesConfirm(true);
  }

  return (
    <>
      <div className="tw-p-3">
        <div className="tw-text-xl tw-font-semibold">Open stage</div>
        <p>New lead has been collected.</p>

        <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-text-sm tw-mb-3">
          <div className="tw-col-span-12 md:tw-col-span-7">
            <Card
              className="tw-mb-3 tw-h-full"
              header={<h3 className="tw-font-semibold tw-text-sm"><FaInfo /> Information</h3>}
            >
              {infoRow('Status:', (
                <><Badge variant="primary" size="sm">{status?.name}</Badge> / Stage: <Badge variant="info" size="sm">{lead.stage}</Badge></>
              ))}
              <div className="tw-mb-4">
                {infoRow('MQL status:', <Badge variant="primary" size="sm">{lead.mql_status}</Badge>)}
              </div>

              {infoRow('Lead name:', <span className="tw-text-blue-600">{lead.name}</span>)}
              {infoRow('Rating:', <RatingScore rating={lead.rating} />)}

              <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2 tw-mt-8">
                <div className="tw-col-span-12 sm:tw-col-span-6">
                  {infoRow('Lead source:', <span className="tw-text-blue-600">{lead.source}</span>, 6)}
                </div>
                <div className="tw-col-span-12 sm:tw-col-span-6">
                  {infoRow('How to know Pebsteel:', <span className="tw-text-blue-600">{lead.contact_mode}</span>, 6)}
                </div>
              </div>
            </Card>
          </div>
          <div className="tw-col-span-12 md:tw-col-span-5">
            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-semibold tw-text-sm"><MdAccountBalance /> Account name</h3>}
            >
              <div className="tw-mb-3">
                {infoRow('Name', <span className="tw-text-blue-600 tw-font-bold">{lead.account_name}</span>, 2)}
              </div>
              {infoRow(<><FaIndustry /> Industry</>, <span className="tw-text-blue-600">{lead.industry}</span>, 5)}
              {infoRow(<><MdBloodtype /> Type of company</>, <span className="tw-text-blue-600">{lead.type_of_company}</span>, 5)}
              {infoRow(<><FaGg /> Legal status</>, <span className="tw-text-blue-600">{lead.legal_status}</span>, 5)}

              <div className="tw-mt-4">
                {infoRow(<><MdEmail /> Work email</>, <Link to={`mailto:${lead.work_email}`}>{lead.work_email}</Link>, 5)}
              </div>
              {infoRow(<><FaPhone /> Work phone</>, <Link to={formatPhoneNumberUrl(lead.work_phone)}>{formatPhoneNumberInternational(lead.work_phone)}</Link>, 5)}
              {infoRow(<><FaLink /> Website</>, <Link to={`//${lead.website}`} target="_blank" rel="noopener noreferrer">{lead.website}</Link>, 5)}
            </Card>

            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-semibold tw-text-sm"><FaMap /> Address</h3>}
            >
              {lead.address ? (
                <GoogleMapsLink address={lead?.address} />
              ) : "No address found."}
            </Card>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-text-sm tw-mb-3">
          <div className="tw-col-span-12 md:tw-col-span-7">
            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-semibold tw-text-sm"><MdMessage /> Customer message</h3>}
            >
              <span className="tw-text-blue-600">{lead.message ? lead.message : "No message found."}</span>
            </Card>

            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-semibold tw-text-sm"><FaRProject /> Project information</h3>}
            >
              <div className="tw-mb-4">
                {infoRow('Project name:', <span className="tw-text-blue-600 tw-font-bold">{lead.project_name}</span>)}
              </div>
              {infoRow('Amount:', <span className="tw-text-blue-600">${formatNumber(lead.amount)}</span>)}
              {infoRow('Area:', <span className="tw-text-blue-600">{formatNumber(lead.area)} m2</span>)}

              <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
                <div className="tw-col-span-12 sm:tw-col-span-6">
                  {infoRow('Building type L1:', <span className="tw-text-blue-600">{lead?.building_type_l1?.name}</span>, 6)}
                </div>
                <div className="tw-col-span-12 sm:tw-col-span-6">
                  {infoRow('Building type L2:', <span className="tw-text-blue-600">{lead?.building_type_l2?.name}</span>, 6)}
                </div>
              </div>

              <div className="tw-mt-4">
                {infoRow('Location:', <GoogleMapsLink address={lead.project_location} />)}
              </div>
            </Card>
          </div>
          <div className="tw-col-span-12 md:tw-col-span-5">
            <Card
              className="tw-mb-3"
              header={<h3 className="tw-font-semibold tw-text-sm"><FaAddressCard /> Contacts</h3>}
            >
              {contacts && contacts.map(contact => (
                <ContactItem
                  key={contact?.id}
                  contact={contact}
                />
              ))}
              {/* <div className="tw-text-right">
                <Button size="sm" variant="primary" onClick={handleAddContact}><FaPlus /> Add contact</Button>
              </div> */}
            </Card>
          </div>
        </div>

        <div className="tw-bg-slate-50 tw-fixed tw-bottom-0 tw-inset-x-0 tw-z-10 tw-text-center tw-p-3">
          {allow_record && (
            <Button
              variant="warning"
              className="tw-mr-1"
              size="sm"
              onClick={handleRecordLead}
            >
              <FaRecordVinyl className="tw-text-red-600"/> <span className="tw-hidden xl:tw-inline-block tw-ml-1">Record</span>
            </Button>
          )}

          {allow_transfer && (
            <Button
              variant="info"
              className="tw-mr-1"
              size="sm"
              onClick={handleTransferToSalesLead}
            >
              <MdTransferWithinAStation className="tw-text-yellow-500"/> <span className="tw-hidden xl:tw-inline-block tw-ml-1">Transfer to Sales</span>
            </Button>
          )}
        </div>
      </div>

      <Suspense fallback={<div>Loading...</div>}>
        {showRecordConfirm && (
          <LazyLeadRecordConfirm
            showModal={showRecordConfirm}
            setshowModal={setShowRecordConfirm}
            lead={lead}
          />
        )}

        {showTransferToSalesConfirm && (
          <LazyLeadTransferToSalesConfirm
            showModal={showTransferToSalesConfirm}
            setshowModal={setShowTransferToSalesConfirm}
            lead={lead}
          />
        )}
      </Suspense>
    </>
  )
};

Open.propTypes = {
  lead: PropTypes.object.isRequired
}

export default Open;
